import React, { useCallback, useEffect, useMemo } from 'react';

import { Checkbox } from '@atlaskit/checkbox';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import TextField from '@atlaskit/textfield';
import memoizeOne from 'memoize-one';

import CheckboxedSection from 'src/views/Admin/Modals/EditRuleModal/RuleForm/components/CheckboxedSection';
import RowLayout3Components from 'src/views/Admin/Modals/EditRuleModal/RuleForm/components/RowLayout3Components';
import { useSelectedServices } from 'src/views/Admin/Modals/EditRuleModal/RuleForm/components/RuleFormContext';
import SelectActions from 'src/views/Admin/Modals/EditRuleModal/RuleForm/components/SelectActions';
import { useApplicableActions } from 'src/views/Admin/Modals/EditRuleModal/RuleForm/components/SelectActions/actions';
import SelectWhenHaveOptions from 'src/views/Admin/Modals/EditRuleModal/RuleForm/components/SelectWhenHaveOptions';
import AutoscalableInputsArray from 'src/views/components/Form/AutoscalableInputsArray';
import Field from 'src/views/components/Form/Field';

const InfoMessage = () => {
  return (
    <ul>
      <li>Each Jira issue can only create a single PagerDuty incident. These rules will be ignored if an incident is already linked</li>
      <li>An incident is created when the issue matches the JQL entered</li>
      <li>Each JQL should route to a single PagerDuty service</li>
      <li>If left unchecked, you can still manually create incidents from a Jira issue</li>
    </ul>
  );
};

const fixedActions = [{ label: 'Create incident', id: '__fixed_create_incident', icon: 'pd' }];

const TargetServiceSelectView = ({ name, values = {}, touched = {}, errors = {}, onChange, onBlur }) => {
  const services = useSelectedServices();
  const actions = useApplicableActions({ issueUpdateActions: true, incidentCreateActions: true });
  const showServiceSelect = Boolean(services && services.length > 1);
  const target_service = values.target_service;

  useEffect(() => {
    // clear target_service if we hiding an input for that value
    if (showServiceSelect === false && target_service) {
      onChange({ target: { name: `${name}.target_service`, value: null } });
    }
  }, [name, onChange, showServiceSelect, target_service]);

  return (
    <div style={{ width: '100%' }}>
      {showServiceSelect && <div style={{ width: '50%' }}>
        <Field label="Create incident into service" errorMessage={touched.target_service && errors.target_service}>
          <SelectWhenHaveOptions
            useOptions={useSelectedServices}
            name={`${name}.target_service`}
            value={values.target_service}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Field>
      </div> }
      <div style={{ width: '100%' }}>
        <Field label="Additional actions after incident is created (optional)">
          <SelectActions
            fixedActions={fixedActions}
            actions={actions}
            name={name}
            values={values}
            touched={touched}
            errors={errors}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Field>
      </div>
    </div>
  );
};

const SingleCreationRule = ({ name, values = {}, touched = {}, errors = {}, onChange: onChangeProp, onBlur }) => {
  // automatically set on_create and on_update to true when user starts entering JQL
  const onChange = useCallback(({ target }) => {
    const isJqlEntered = target.name === `${name}.jql` && target.value && !values.jql;
    if (isJqlEntered && !values.on_create && !values.on_update) {
      onChangeProp({
        target: {
          name,
          value: {
            ...values,
            jql: target.value,
            on_create: true,
            on_update: true
          }
        }
      });
    } else {
      onChangeProp({ target });
    }
  }, [name, onChangeProp, values]);

  const jqlWarning = useMemo(() => memoizeOne((jql) => {
    if (typeof jql !== 'string') return null;
    if (jql.match(/(^|\s)status((\s*=)|(\s+in(\W|$)))/i)) {
      return (
        <span>
          JQL on issue status might not work:{' '}
          <a href="https://jira.atlassian.com/browse/JRASERVER-65174" target="_blank" rel="noopener noreferrer">JRASERVER-65174</a>.
        </span>
      );
    }
    return null;
  }), []);

  return (
    <RowLayout3Components>
      <div>
        <Field label="when issue matches JQL:" errorMessage={touched.jql && errors.jql} warningMessage={jqlWarning(values.jql)}>
          <TextField
            name={`${name}.jql`}
            value={values.jql || ''}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Field>
        <div style={{ display: 'flex' }}>
          <Checkbox
            isChecked={Boolean(values.on_create)}
            onChange={onChange}
            name={`${name}.on_create`}
            label="On issue create"
          />
          <Checkbox
            isChecked={Boolean(values.on_update)}
            onChange={onChange}
            name={`${name}.on_update`}
            label="On issue update"
          />
        </div>
      </div>
      <div style={{ paddingTop: 18 }}>
        <ArrowRightIcon size="xlarge"/>
      </div>
      <TargetServiceSelectView
        name={name}
        values={values}
        touched={touched}
        errors={errors}
        onChange={onChange}
        onBlur={onBlur}
      />
    </RowLayout3Components>
  );
};

const newItemValue = { jql: '', on_create: false, on_update: false, target_service: null };
const isItemEmpty = ({ jql, on_create, on_update } = {}) => {
  return !jql && !on_create && !on_update;
};

const CreateIncident = ({ name, values = {}, touched = {}, errors = {}, onChange, onBlur }) => {
  return (
    <div>
      <CheckboxedSection
        name={`${name}.autocreate_incidents`}
        value={values.autocreate_incidents}
        onChange={onChange}
        onBlur={onBlur}
        label="Automatically create PagerDuty incidents from Jira issues"
        helpMessage={<InfoMessage/>}
      >
        <div style={{ height: '20px' }}/>
        <AutoscalableInputsArray
          name={`${name}.autocreate_incidents_config`}
          values={values.autocreate_incidents_config}
          touched={touched.autocreate_incidents_config}
          errors={errors.autocreate_incidents_config}
          onChange={onChange}
          onBlur={onBlur}
          ItemComponent={SingleCreationRule}
          isItemEmpty={isItemEmpty}
          newItemValue={newItemValue}
        />
        <div style={{ height: '50px' }}/>
      </CheckboxedSection>
    </div>
  );
};

export default CreateIncident;
